import { adminUtils, info, log, timeUtils, userUtils, utils } from '@/helpers';
import { CampaignBuilderStore, useCampaignBuilderStore, useCoMarketingStore, useUserSettingsEditingStore, useUserSettingsStore } from '@/stores';
import {
	ABChannel,
	ABChannelType,
	ABVariant,
	AdTemplate,
	BrowserTemplate,
	BudgetForecastOutput,
	Campaign,
	CampaignChannelType,
	CampaignChannelUnion,
	CampaignMessageCaps,
	CampaignSchedule,
	CampaignScheduleType,
	CampaignSendingType,
	CampaignTargeting,
	CampaignTriggerType,
	ConversionActionType,
	EmailTemplate,
	InstalledCoMarketingTemplate,
	PushTemplate,
	SMSTemplate,
	SnailMailTemplate,
	SponsoredCoMarketingTemplate,
	VoiceTemplate,
} from '@/types';
import { IconType } from '@alpine-iq/ui-library';
import { clone, cloneDeep, max } from 'lodash';
import {
	getDefaultAdTemplate,
	getDefaultBrowserTemplate,
	getDefaultEmailTemplate,
	getDefaultPushTemplate,
	getDefaultSMSTemplate,
	getDefaultSnailMailTemplate,
	getDefaultVoiceTemplate,
} from './utils/getDefaultChannel';
import { safeFetchAPI } from '@/api';
import { isChannelEmpty } from '../legacyCampaigns/builder/cmp.builder.helpers';
import { getEffectiveTimezone } from '@/helpers/timezone';

export function getDefaultCampaign(): Partial<Campaign> {
	const { globalSettings } = useUserSettingsEditingStore.getState();
	const defaultAttributionWindow = globalSettings?.campaignSettings?.defaultAttributionWindow;
	const hasDefaultActions = (defaultAttributionWindow?.actions?.length ?? 0) > 0;

	log('defaultAttributionWindow', defaultAttributionWindow);
	log('globalSettings', globalSettings);

	return {
		draft: true,
		blast: true,
		type: 'TEXT',
		recurDays: -1,
		userID: utils.uid || utils.user?.id,
		conversionWindow: defaultAttributionWindow?.count || 14,
		sendingType: 'regular',
		timezone: getEffectiveTimezone(),
		conversionActions: hasDefaultActions ? defaultAttributionWindow?.actions : getDefaultConversionActions(),
	};
}

export function getDefaultCoMarketing(): Partial<SponsoredCoMarketingTemplate> {
	return {
		type: 'sponsored',
		budgetMethod: 'disabled',
		userID: utils.uid,
		responsibleEmails: [utils.auth.getEmail()],
	};
}

function getDefaultConversionActions(): ConversionActionType[] {
	const actionKeys = Object.keys(CampaignConversionActionMetaMap) as ConversionActionType[];
	return actionKeys.filter((key) => CampaignConversionActionMetaMap[key].defaultEnabled);
}

type CampaignBuilderStep = {
	name: string;
	isShown: (campaign?: Partial<Campaign>) => boolean;
	canGoNext: (campaign?: Partial<Campaign>) => [boolean, string];
};

type CampaignBuilderStepType = 'channels' | 'recipients' | 'content' | 'variants' | 'coMarketing' | 'review';

export const CampaignBuilderSteps: Record<CampaignBuilderStepType, CampaignBuilderStep> = {
	channels: {
		name: 'Channels',
		isShown(campaign) {
			return true;
		},
		canGoNext(campaign) {
			// Ensure the campaign has at least one channel
			if (!hasCampaignChannels(campaign)) {
				return [false, 'Please select at least one channel'];
			}

			return [true, ''];
		},
	},
	recipients: {
		name: 'Recipients',
		isShown(campaign) {
			return true;
		},
		canGoNext(campaign) {
			// Check previous steps
			const channelsStep = CampaignBuilderSteps['channels'];
			const [channelsNextStep, channelsError] = channelsStep.canGoNext(campaign);
			if (!channelsNextStep) {
				return [false, channelsError];
			}

			// Make sure campaign has a name
			if (!campaign?.name) {
				return [false, 'Please enter a name for your campaign'];
			}

			// Make sure they have at least some targeting
			const targeting = getCampaignTargeting(campaign);
			if (!targeting?.audiences?.length && !targeting?.surveys?.length && !targeting?.signupForms?.length) {
				return [false, 'Please select at least one audience, survey, or signup form target'];
			}

			return [true, ''];
		},
	},
	variants: {
		name: 'Variants',
		isShown(campaign) {
			return isVariantCampaign(campaign);
		},
		canGoNext(campaign) {
			return [true, ''];
		},
	},
	content: {
		name: 'Content',
		isShown(campaign) {
			return true;
		},
		canGoNext(campaign) {
			// Make sure they've completed recipients & channels step
			const recipientsStep = CampaignBuilderSteps['recipients'];
			const channelsStep = CampaignBuilderSteps['channels'];

			const [nextStep, channelsError] = channelsStep.canGoNext(campaign);
			if (!nextStep) {
				return [false, channelsError];
			}

			const [recipientsNextStep, recipientsError] = recipientsStep.canGoNext(campaign);
			if (!recipientsNextStep) {
				return [false, recipientsError];
			}

			return [true, ''];
		},
	},
	coMarketing: {
		name: 'Co-Marketing',
		isShown(campaign) {
			if (campaign?.coMarketing?.installedFromUserID) {
				return true;
			}
			return campaign?.isCoMarketing ?? false;
		},
		canGoNext(campaign) {
			return [true, ''];
		},
	},
	review: {
		name: 'Review',
		isShown(campaign) {
			return true;
		},
		canGoNext(campaign) {
			return [true, ''];
		},
	},
};

export const CampaignFormBuilderStepKeys = Object.keys(CampaignBuilderSteps) as CampaignBuilderStepType[];
export type CampaignFormBuilderStepType = keyof typeof CampaignBuilderSteps;

export type CampaignTypeInfo = {
	name: string;
	description: string;
	disabled?: (campaign?: Partial<Campaign>) => boolean;
	isSelected: (campaign?: Partial<Campaign>) => boolean;
	applyToCampaign: (campaign?: Partial<Campaign>) => Partial<Campaign>;
};

export const CampaignTypes: Record<CampaignSendingType, CampaignTypeInfo> = {
	regular: {
		name: 'Regular',
		description: 'Each selected channel will be sent to the selected audience.',
		isSelected(campaign) {
			return campaign?.sendingType === 'regular';
		},
		applyToCampaign(campaign) {
			return { ...campaign, sendingType: 'regular', abVariants: undefined };
		},
		disabled(campaign) {
			// Cant change from type it was installed as
			if (campaign?.coMarketing?.installedFromTemplateID) {
				return true;
			}
			// Is ab variant campaign
			return false;
		},
	},
	variant: {
		name: 'A/B Test',
		description: 'Send different campaign variations to see what performs best.',
		isSelected(campaign) {
			return campaign?.sendingType === 'variant';
		},
		applyToCampaign(campaign) {
			const existingChannels = getCampaignsChannels(campaign, true);
			if (!campaign) return {};

			if (campaign.isCoMarketing) {
				utils?.toaster?.addToast?.({
					message: 'A/B test campaigns cannot be used as co-marketing campaigns',
					type: 'danger',
				});

				return campaign;
			}

			// If they have more than a single text or single email channel, we need to ask them to select a single channel
			if (existingChannels.length > 1) {
				utils?.toaster?.addToast?.({
					message: 'A/B test campaigns must have a single text or email channel',
					type: 'danger',
				});

				return campaign;
			}

			const abVariants: ABVariant[] = [];
			for (const [channel, _] of existingChannels) {
				if (channel === CampaignChannelType.Email || channel === CampaignChannelType.Text) {
					const campaignChannel: ABChannel | undefined = campaign?.[channel];
					if (campaignChannel) {
						abVariants.push({
							id: utils.getUUID(),
							percentage: 1,
							[channel]: campaignChannel,
						});
					}
				}
				campaign[channel] = undefined;
			}

			return { ...campaign, sendingType: 'variant', abVariants: abVariants };
		},
		disabled(campaign) {
			// Cant change from type it was installed as
			if (campaign?.coMarketing?.installedFromTemplateID) {
				return true;
			}
			return !!campaign?.isCoMarketing;
		},
	},
	waterfall: {
		name: 'Waterfall',
		description: 'Audience receives the first channel they are opted into, in cascading order.',
		isSelected(campaign) {
			return campaign?.sendingType === 'waterfall' || !campaign?.sendingType;
		},
		applyToCampaign(campaign) {
			return { ...campaign, sendingType: 'waterfall', abVariants: undefined };
		},
		disabled(campaign) {
			// Cant change from type it was installed as
			if (campaign?.coMarketing?.installedFromTemplateID) {
				return true;
			}
			if (campaign?.isCoMarketing) {
				return true;
			}
			return false;
		},
	},
};

export type CampaignTriggerInfo = {
	name: string;
	description: string;
	isHidden?: (campaign?: Partial<Campaign>) => boolean;
	isEnabled?: (campaign?: Partial<Campaign>) => boolean;
	applyToCampaign: (campaign?: Partial<Campaign>) => Partial<Campaign>;
};

export const CampaignTriggerTypes: Record<CampaignTriggerType, CampaignTriggerInfo> = {
	normal: {
		name: 'Normal',
		description: 'A normal campaign',
		isEnabled(campaign) {
			// Check all of the othe types are not enabled
			return Object.entries(CampaignTriggerTypes).every(([key, trigger]) => key === 'normal' || !trigger.isEnabled?.(campaign));
		},
		applyToCampaign(campaign) {
			const newCampaign = clearTriggers(campaign);
			return newCampaign;
		},
	},
	abandonedCart: {
		name: 'Abandoned Cart',
		description: 'Automatically trigger a send when a customer abandons their cart',
		isEnabled(campaign) {
			return !!campaign?.triggers?.abandonedCart;
		},
		applyToCampaign(campaign) {
			const newCampaign = clearTriggers(campaign);
			newCampaign.triggers = { abandonedCart: true };
			return newCampaign;
		},
	},
	postPurchase: {
		name: 'Post Purchase',
		description: 'Automatically trigger a send when a customer makes a purchase',
		isEnabled(campaign) {
			return !!campaign?.triggers?.postSale;
		},
		applyToCampaign(campaign) {
			const newCampaign = clearTriggers(campaign);
			newCampaign.triggers = { postSale: true };
			return newCampaign;
		},
	},
	birthdayReward: {
		name: 'Birthday Reward',
		description: "Automatically trigger a send on a customer's birthday",
		isEnabled(campaign) {
			return !!campaign?.triggers?.birthday;
		},
		applyToCampaign(campaign) {
			const newCampaign = clearTriggers(campaign);
			newCampaign.triggers = { birthday: true };
			return newCampaign;
		},
	},
	randomLottery: {
		name: 'Random Lottery',
		description: 'Trigger a send to a random selection of your audience per day',
		isEnabled(campaign) {
			return !!campaign?.capPerDay;
		},
		applyToCampaign(campaign) {
			const newCampaign = clearTriggers(campaign);
			newCampaign.capPerDay = 1;
			return newCampaign;
		},
	},
};

function clearTriggers(campaign?: Partial<Campaign>): Partial<Campaign> {
	if (!campaign) return campaign ?? {};
	const newCampaign = cloneDeep(campaign);
	newCampaign.capPerDay = undefined;
	newCampaign.triggers = undefined;
	newCampaign.optinCampaign = false;
	newCampaign.emailOptinCampaign = false;
	return newCampaign;
}

function applyVariantTemplate(campaign: Partial<Campaign>, channel: ABChannelType, template: ABChannel): Partial<Campaign> {
	// If there are no variants apply 2 empty variant objects with 50/50
	const variant: Omit<ABVariant, 'id'> = {
		percentage: 0.5,
		[channel as CampaignChannelType]: template,
	};

	const abVariants = [
		clone({
			...variant,
			id: utils.getUUID(),
		}),
		clone({
			...variant,
			id: utils.getUUID(),
		}),
	];

	log('Applying variant template', channel, template, abVariants);

	return { ...campaign, abVariants };
}

function removeVariantTemplate(campaign: Partial<Campaign>, channel: ABChannelType) {
	const abVariants = campaign.abVariants ?? [];

	for (const variant of abVariants) {
		variant[channel] = undefined;
	}

	if (isABVariantEmpty(campaign)) {
		campaign.abVariants = undefined;
	}

	return { ...campaign, abVariants };
}

function isABVariantEmpty(campaign: Partial<Campaign>) {
	const abVariants = campaign.abVariants ?? [];
	const loopKeys = [CampaignChannelType.Email, CampaignChannelType.Text] as const;
	return abVariants.every((variant) => loopKeys.every((key) => !variant[key]));
}

function getDefaultChannel(campaign: Partial<Campaign>, channel: CampaignChannelType): CampaignChannelUnion {
	switch (channel) {
		case CampaignChannelType.Email:
			return getDefaultEmailTemplate(campaign);
		case CampaignChannelType.Text:
			return getDefaultSMSTemplate(campaign);
		case CampaignChannelType.Voice:
			return getDefaultVoiceTemplate(campaign);
		case CampaignChannelType.Push:
			return getDefaultPushTemplate(campaign);
		case CampaignChannelType.Browser:
			return getDefaultBrowserTemplate(campaign);
		case CampaignChannelType.SnailMail:
			return getDefaultSnailMailTemplate(campaign);
		case CampaignChannelType.Ad:
			return getDefaultAdTemplate(campaign);
		default:
			return {} as CampaignChannelUnion;
	}
}

export function getNextChannelPriority(campaign: Partial<Campaign>): number {
	const campaignChannels = getCampaignsChannels(campaign, true);
	return campaignChannels.length;
}

/*
	Adds a channel to the campaign
	Returns the updated campaign object	
*/
function addChannelToCampaign(campaign: Partial<Campaign>, channel: CampaignChannelType) {
	const template = getDefaultChannel(campaign, channel);

	log('Adding campaign channel', channel, template, 'isVariant', isVariantCampaign(campaign));

	if (isVariantCampaign(campaign)) {
		if (channel === CampaignChannelType.Email || channel === CampaignChannelType.Text) {
			return applyVariantTemplate(campaign, channel, template);
		}
		return campaign;
	}

	return { [channel]: { ...template, priority: getNextChannelPriority(campaign) } };
}

function removeChannel(campaign: Partial<Campaign>, channel: CampaignChannelType) {
	log('Removing campaign channel', channel, 'isVariant', isVariantCampaign(campaign));
	if (isVariantCampaign(campaign)) {
		if (channel === CampaignChannelType.Email || channel === CampaignChannelType.Text) {
			removeVariantTemplate(campaign, channel);
		}
		return campaign;
	}

	getCampaignsChannels(campaign, true).forEach(([chan], priority) => {
		campaign[chan] = { ...campaign[chan], priority };
	});

	return { ...campaign, [channel]: undefined };
}

export function toggleChannel(campaign?: Partial<Campaign>, channel?: CampaignChannelType) {
	if (!campaign || !channel) return campaign;
	if (hasCampaignChannel(campaign, channel)) {
		return removeChannel(campaign, channel);
	}
	return addChannelToCampaign(campaign, channel);
}

export function setCampaignChannelPriority(
	campaign: Partial<Campaign>,
	channel: CampaignChannelType,
	priority: number,
): {
	campaign: Partial<Campaign>;
	changed: boolean;
} {
	// IF this campaign doesn't have this channel.. don't allow
	if (!hasCampaignChannel(campaign, channel)) {
		return { campaign, changed: false };
	}

	const typeByIndex = new Map<number, CampaignChannelType>();

	const campaignChannels = getCampaignsChannels(campaign, true).map(([channel, _], index) => {
		typeByIndex.set(index, channel);
		return channel;
	});
	const maxPriority = campaignChannels.length - 1;

	const currentPriority = campaign[channel]?.priority || 0;
	const newPriority = Math.max(0, Math.min(priority, maxPriority));

	log('Setting priority', channel, maxPriority, currentPriority, newPriority);

	if (currentPriority === newPriority) {
		log('No change in priority', channel, currentPriority, newPriority);
		return { campaign, changed: false };
	}

	const hasCurrentPriority = currentPriority !== undefined;
	const shiftingLeft = hasCurrentPriority && currentPriority > newPriority;
	const shiftingRight = hasCurrentPriority && currentPriority < newPriority;
	const distance = Math.abs(newPriority - currentPriority);

	// If we are shifting over by just one, we can just swap the priorities
	if (distance === 1) {
		// Find the channel type we are swapping with
		const swapType = typeByIndex.get(newPriority);
		if (!swapType) {
			log('No swap type found', newPriority);
			return { campaign, changed: false };
		}
		campaign[swapType] = { ...campaign[swapType], priority: currentPriority };
		campaign[channel] = { ...campaign[channel], priority: newPriority };

		log('Swapped priority', channel, currentPriority, newPriority);
		return { campaign: clone(campaign), changed: true };
	}

	const channelsToUpdate = campaignChannels
		.filter((chan) => (campaign[chan]?.priority ?? 0) !== currentPriority)
		.filter((chan) => {
			const isNewPriority = (campaign[chan]?.priority ?? 0) === newPriority;
			const isAboveNewPriority = (campaign[chan]?.priority ?? 0) > newPriority && shiftingLeft;
			const isBelowNewPriority = (campaign[chan]?.priority ?? 0) < newPriority && shiftingRight;
			return isNewPriority || isAboveNewPriority || isBelowNewPriority;
		});

	// Update the priority of the channels in between
	for (const chan of channelsToUpdate) {
		const channelPriority = campaign[chan]?.priority;
		if (channelPriority === undefined) {
			continue;
		}
		const isAboveNewPriority = channelPriority >= newPriority && shiftingLeft;
		const isBelowNewPriority = channelPriority <= newPriority && shiftingRight;
		if (isAboveNewPriority && channelPriority < currentPriority) {
			campaign[chan] = { ...campaign[chan], priority: channelPriority + 1 };
			log(`Chan: ${chan} | Priority: ${channelPriority} | New Priority: ${channelPriority + 1}. (Is Above)`);
		}
		if (isBelowNewPriority && channelPriority > currentPriority) {
			campaign[chan] = { ...campaign[chan], priority: channelPriority - 1 };
			log(`Chan: ${chan} | Priority: ${channelPriority} | New Priority: ${channelPriority - 1}. (Is Below)`);
		}
	}

	// Set the priority of the channel
	campaign[channel] = { ...campaign[channel], priority: newPriority };

	log('Set priority', channel, currentPriority, newPriority);
	return { campaign: clone(campaign), changed: true };
}

export function validateCampaignChannelPriority(campaign: Campaign): Campaign {
	getCampaignsChannels(campaign, true)
		// Filter out channels that are not selected
		.filter(([channel, _]) => campaign[channel]?.priority !== undefined)
		// Map to channel and priority
		.map(([channel, _]) => ({ channel, priority: campaign[channel]?.priority as number }))
		// Sort by priority
		.sort((a, b) => a.priority - b.priority)
		// Update channels with invalid priority and return the changes
		.forEach((chan, index) => {
			const { channel, priority } = chan;
			if (priority > index) {
				campaign[channel] = { ...campaign[channel], priority: index };
			}
		});
	return campaign;
}

type MetaCtx = {
	emailReady?: boolean;
};

export type CampaignChannelInfo = {
	icon: IconType;
	shortName: string;
	description: string;
	defaultPriority: number;
	isVisibleForUser?: () => boolean;
	canBeAdded?: (campaign?: Partial<Campaign>, context?: MetaCtx) => [boolean, string];
};

export const CampaignChannelsMetaMap: Record<CampaignChannelType, CampaignChannelInfo> = {
	[CampaignChannelType.Email]: {
		icon: 'EMAIL_NEW',
		shortName: 'Email',
		description: 'Email',
		defaultPriority: 0,
		canBeAdded(campaign, context) {
			// If they are a brand & the campaign is CoMarketing they can skip
			const isBrandCoMarketing = utils.isBrand() && campaign?.isCoMarketing;
			if (isBrandCoMarketing || utils.isBrandOnly()) {
				return [true, ''];
			}

			if (!context?.emailReady) {
				// Need to configure email domain sedttings
				return [false, 'Verify your domain using the Email Settings page to send emails'];
			}

			return [true, ''];
		},
	},
	[CampaignChannelType.Text]: {
		icon: 'CHAT_BUBBLE',
		shortName: 'Text',
		description: 'Text message',
		defaultPriority: 1,
		canBeAdded(campaign) {
			return [true, ''];
		},
	},
	[CampaignChannelType.Voice]: {
		icon: 'TELEPHONE',
		shortName: 'Voice',
		description: 'Voice call',
		defaultPriority: 2,
		canBeAdded(campaign) {
			if (isVariantCampaign(campaign)) {
				return [false, 'Voice is not available for A/B test campaigns.'];
			}
			return [true, ''];
		},
	},
	[CampaignChannelType.Push]: {
		icon: 'PHONE',
		shortName: 'Native Push',
		description: 'Native app push',
		defaultPriority: 3,
		canBeAdded(campaign) {
			if (isVariantCampaign(campaign)) {
				return [false, 'Native push is not available for A/B test campaigns.'];
			}

			// If they are a brand & the campaign is CoMarketing they can skip
			const isBrandCoMarketing = utils.isBrand() && campaign?.isCoMarketing;
			if (isBrandCoMarketing || utils.isBrandOnly()) {
				return [true, ''];
			}

			if (!utils.user?.metadata?.hasNativeApp && !utils.user?.hasCustomApp) {
				return [false, 'Push is not available until you have a native app. Please contact support to get started.'];
			}
			return [true, ''];
		},
	},
	[CampaignChannelType.Browser]: {
		icon: 'BROWSER',
		shortName: 'Browser Push',
		description: 'Browser push',
		defaultPriority: 4,
		canBeAdded(campaign) {
			if (isVariantCampaign(campaign)) {
				return [false, 'Browser push is not available for A/B test campaigns.'];
			}
			return [true, ''];
		},
	},
	[CampaignChannelType.SnailMail]: {
		icon: 'MAIL',
		shortName: 'Direct Mail',
		description: 'Direct mail',
		defaultPriority: 5,
		canBeAdded(campaign) {
			const hasMarketingPro = userUtils.hasPackageTier('marketing', 'pro');
			if (!hasMarketingPro) {
				return [false, 'Direct mail is available with the Marketing Pro subscription.'];
			}
			if (isVariantCampaign(campaign)) {
				return [false, 'Direct mail is not available for A/B test campaigns.'];
			}
			return [true, ''];
		},
	},
	[CampaignChannelType.Ad]: {
		icon: 'MARKETING',
		shortName: 'Screen Ad',
		description: 'Screen ad',
		defaultPriority: 6,
		isVisibleForUser: () => false,
		canBeAdded(campaign) {
			if (isVariantCampaign(campaign)) {
				return [false, 'Screen ads are not available for A/B test campaigns.'];
			}
			return [true, ''];
		},
	},
} as const;

export const CampaignChannelsMeta = Object.keys(CampaignChannelsMetaMap) as CampaignChannelType[];

export type ConvesionTypeData = {
	name: string;
	description: string;
	defaultEnabled: boolean; // Will this be selected by default to be counted as a conversion?
	template: CampaignChannelType | undefined;
};

export const CampaignConversionActionMetaMap: Record<ConversionActionType, ConvesionTypeData> = {
	text_received: {
		defaultEnabled: true,
		name: 'Text received',
		description: 'After a text is sent',
		template: CampaignChannelType.Text,
	},
	text_click_to_open: {
		defaultEnabled: true,
		name: 'Opened text',
		description: 'After they open a text',
		template: CampaignChannelType.Text,
	},
	text_sent: {
		defaultEnabled: true,
		name: 'Customer response via text',
		description: 'After customer responds to a text',
		template: CampaignChannelType.Text,
	},
	text_click: {
		defaultEnabled: true,
		name: 'Landing page viewed',
		description: 'After landing page link was clicked',
		template: CampaignChannelType.Text,
	},
	email_received: {
		defaultEnabled: true,
		name: 'Email received',
		description: 'After email is sent',
		template: CampaignChannelType.Email,
	},
	email_open: {
		defaultEnabled: true,
		name: 'Opened email',
		description: 'After an email was opened',
		template: CampaignChannelType.Email,
	},
	push_received: {
		defaultEnabled: true,
		name: 'Native push received',
		description: 'After native app push delivered',
		template: CampaignChannelType.Push,
	},
	push_open: {
		defaultEnabled: true,
		name: 'Opened native push',
		description: 'After a push was opened',
		template: CampaignChannelType.Push,
	},
	// ad_received: {
	// 	defaultEnabled: false,
	// 	name: "Screen ad received",
	// 	description: "After an app ad or screen ad was shown",
	// 	template: 'adTemplate'
	// },
	browser_received: {
		defaultEnabled: true,
		name: 'Browser push received',
		description: 'After browser push delivered',
		template: CampaignChannelType.Browser,
	},
	direct_mail_received: {
		defaultEnabled: true,
		name: 'Direct mail received',
		description: 'After a direct mail letter was delievered',
		template: CampaignChannelType.SnailMail,
	},
	call_answer: {
		defaultEnabled: true,
		name: 'Voice drop answered',
		description: 'After call was answered by a person',
		template: CampaignChannelType.Voice,
	},
	call_vm: {
		defaultEnabled: true,
		name: 'Voice drop hit voicemail',
		description: 'After call went to voicemail',
		template: CampaignChannelType.Voice,
	},
	call_complete: {
		defaultEnabled: true,
		name: 'Voice drop completed',
		description: 'After a voice drop is completed',
		template: CampaignChannelType.Voice,
	},
	TerpliReview: {
		defaultEnabled: false,
		name: 'Terpli review left',
		description: 'After a Terpli review',
		template: undefined,
	},
	TerpliRecommendation: {
		defaultEnabled: false,
		name: 'Terpli recommendation left',
		description: 'After a Terpli recommendation',
		template: undefined,
	},
	TerpliReviewDiscount: {
		defaultEnabled: false,
		name: 'Terpli review discount',
		description: 'After a Terpli review discount',
		template: undefined,
	},
	TerpliRecommendationDiscount: {
		defaultEnabled: false,
		name: 'Terpli recommendation discount',
		description: 'After a Terpli recommendation discount',
		template: undefined,
	},
};

/**
 * Returns all campaign channels that are visible for the user
 * @returns [CampaignChannelType, CampaignChannelInfo][]
 */
export function getCampaignsChannels(campaign?: Partial<Campaign>, inUse?: boolean): [CampaignChannelType, CampaignChannelInfo][] {
	const filered = (Object.entries(CampaignChannelsMetaMap) as [CampaignChannelType, CampaignChannelInfo][]).filter(([type, value]) => {
		const isVisible = !value.isVisibleForUser || value.isVisibleForUser?.();
		if (!isVisible) return false;
		if (inUse) return hasCampaignChannel(campaign, type);
		return true;
	});

	if (!isWaterfallCampaign(campaign)) {
		return filered;
	}

	// Sort by the priority on the campaign || the default priority
	const sorted = filered.sort(([a, aI], [b, bI]) => {
		const channelA = campaign?.[a];
		const channelB = campaign?.[b];
		// If both are undefined we sort by default
		if (channelA?.priority === undefined && channelB?.priority === undefined) {
			const defaultA = aI.defaultPriority;
			const defaultB = bI.defaultPriority;
			return defaultA - defaultB;
		}
		// undefined priorities are sorted to the end
		if (channelA?.priority === undefined) return 1;
		if (channelB?.priority === undefined) return -1;
		return (channelA?.priority || 0) - (channelB?.priority || 0);
	});

	return sorted;
}

/**
 * Checks if the campaign has any channels
 * @param campaign
 * @returns true if the campaign has any channels
 */
export function hasCampaignChannels(campaign?: Partial<Campaign>): boolean {
	// Loop over possible campaign types & check if they exist
	for (const type of CampaignChannelsMeta) {
		if (hasCampaignChannel(campaign, type)) {
			return true;
		}
	}
	return false;
}

/**
 * Checks if the campaign is a waterfall campaign
 * @param campaign
 * @returns true if the campaign is a waterfall campaign
 */
export function isWaterfallCampaign(campaign?: Partial<Campaign>): boolean {
	return campaign?.sendingType === 'waterfall' || !campaign?.sendingType;
}

/**
 * Checks if the campaign is a variant campaign
 * @param campaign
 * @returns true if the campaign is a variant campaign
 */
export function isVariantCampaign(campaign?: Partial<Campaign>): boolean {
	return campaign?.sendingType === 'variant';
}

/**
 * Checks if the campaign is a regular campaign
 * @param campaign
 * @returns true if the campaign is a regular campaign
 */
export function isRegularCampaign(campaign?: Partial<Campaign>): boolean {
	return campaign?.sendingType === 'regular';
}

/**
 * Checks if the campaign has a specific channel
 * @param campaign
 * @param channel
 * @returns true if the campaign has the channel
 */
export function hasCampaignChannel(campaign?: Partial<Campaign>, channel?: CampaignChannelType): boolean {
	if (!channel) return false;
	if (isVariantCampaign(campaign)) {
		if (channel === CampaignChannelType.Email || channel === CampaignChannelType.Text) {
			return !!campaign?.abVariants?.some((variant) => variant[channel]?.inUse);
		}
		return false;
	}
	return campaign?.[channel]?.inUse ?? false;
}

/**
 * Checks if the campaign channel has content
 * @param type
 * @param channel
 * @returns true if the campaign channel has content
 */
export function hasCampaignChannelContent(type: CampaignChannelType, channel?: CampaignChannelUnion): boolean {
	if (!channel) return false;
	if (channel.inUse) return true;
	switch (type) {
		case CampaignChannelType.Email: {
			const emailTemplate = channel as EmailTemplate;
			return !!emailTemplate.html;
		}
		case CampaignChannelType.Text: {
			const smsTemplate = channel as SMSTemplate;

			if (smsTemplate.landingType === 'HTML') {
				return !!smsTemplate.landingHTML && !!smsTemplate.msgContainer;
			}

			// Depending on the type of text.. the content field is different
			return !!smsTemplate.msgContainer || !!smsTemplate.body;
		}
		case CampaignChannelType.Voice: {
			const voiceTemplate = channel as VoiceTemplate;
			return !!voiceTemplate.ttv || !!voiceTemplate.voiceFile;
		}
		case CampaignChannelType.Push: {
			const pushTemplate = channel as PushTemplate;
			return !!pushTemplate.title || !!pushTemplate.body;
		}
		case CampaignChannelType.Browser: {
			const browserTemplate = channel as BrowserTemplate;
			return !!browserTemplate.title || !!browserTemplate.body;
		}
		case CampaignChannelType.SnailMail: {
			const snailMailTemplate = channel as SnailMailTemplate;
			return !!snailMailTemplate.front || !!snailMailTemplate.back;
		}
		case CampaignChannelType.Ad: {
			const adTemplate = channel as AdTemplate;
			return !!adTemplate.ad;
		}
		default:
			return false;
	}
}

export function getCampaignBudgetInfo(campaign?: Partial<Campaign>, budget?: BudgetForecastOutput): Partial<Record<CampaignChannelType, ChannelBudgetInfo>> {
	const channels = getCampaignsChannels(campaign, true);
	const info: Partial<Record<CampaignChannelType, ChannelBudgetInfo>> = {};
	for (const [channel, _] of channels) {
		info[channel] = getChannelBudgetInfo(budget, channel);
	}
	return info;
}

export type ChannelBudgetInfo = {
	cost: number;
	sends: number;
};

export function getChannelBudgetInfo(budget: BudgetForecastOutput | undefined, channel: CampaignChannelType): ChannelBudgetInfo | undefined {
	if (!budget) return { cost: 0, sends: 0 };
	switch (channel) {
		case CampaignChannelType.Email: {
			return {
				cost: budget?.budgetEstimates?.costEmail || 0,
				sends: budget?.sendEstimates?.countEmail || 0,
			};
		}
		case CampaignChannelType.Text: {
			const mmsCost = budget?.perSendCosts?.costPerMMS || 0;
			const smsCost = budget?.perSendCosts?.costPerSMS || 0;

			const smsSends = budget?.sendEstimates?.countSMS || 0;
			const mmsSends = budget?.sendEstimates?.countMMS || 0;

			return {
				cost: mmsCost + smsCost,
				sends: smsSends + mmsSends,
			};
		}
		case CampaignChannelType.Voice: {
			return {
				cost: budget?.budgetEstimates?.costVoice || 0,
				sends: budget?.sendEstimates?.countVoice || 0,
			};
		}
		case CampaignChannelType.Push: {
			return {
				cost: budget?.budgetEstimates?.costPush || 0,
				sends: budget?.sendEstimates?.countPush || 0,
			};
		}
		case CampaignChannelType.Browser: {
			return {
				cost: budget?.budgetEstimates?.costBrowser || 0,
				sends: budget?.sendEstimates?.countBrowser || 0,
			};
		}
		case CampaignChannelType.SnailMail: {
			return {
				cost: budget?.budgetEstimates?.costMail || 0,
				sends: budget?.sendEstimates?.countPostal || 0,
			};
		}
		case CampaignChannelType.Ad: {
			return {
				cost: budget?.budgetEstimates?.costAd || 0,
				sends: budget?.sendEstimates?.countAd || 0,
			};
		}
		default:
			return {
				cost: 0,
				sends: 0,
			};
	}
}

export async function sendTestEmail(recipient: string, templateData: EmailTemplate): Promise<boolean> {
	const lastSend = utils.local.get('lastTestEmailSend');
	const now = Date.now();
	const COOLDOWN = 30 * 1000;
	if (lastSend && now - lastSend < COOLDOWN) {
		const timeRemaining = Math.round((COOLDOWN - (now - lastSend)) / 1000);
		utils?.toaster?.addToast?.({
			message: `Please wait ${timeRemaining} seconds before sending another test email`,
			type: 'danger',
		});
		return false;
	}

	const email = recipient?.trim();
	if (!email) {
		utils?.toaster?.addToast?.({
			message: 'Missing email to send test to',
			type: 'danger',
		});
		return false;
	}

	if (!templateData.subject) {
		utils?.toaster?.addToast?.({
			message: 'Missing subject',
			type: 'danger',
		});
		return false;
	}

	if (!templateData.from) {
		utils?.toaster?.addToast?.({
			message: 'Missing from',
			type: 'danger',
		});
		return false;
	}

	if ((templateData.html?.length || 0) >= 300_000 && utils.uid !== '1033') {
		utils?.toaster?.addToast?.({
			message: 'Email body is too large',
			type: 'danger',
		});
		return false;
	}

	// &amp; is breaking the links so we need to remove it.
	templateData.html = templateData.html?.replaceAll(/&amp;/g, '&');

	const [_, error] = await safeFetchAPI(`/sendTestEmail/${utils.uid}/${encodeURIComponent(email)}`, {
		method: 'POST',
		payload: { emailTemplate: templateData },
	});

	if (error) {
		utils?.toaster?.addToast?.({
			message: error,
			type: 'danger',
		});
		return false;
	}

	utils?.toaster?.addToast?.({
		message: `Test email sent to ${email}`,
		type: 'success',
	});

	utils.local.set('lastTestEmailSend', now);

	return true;
}

export async function sendTestText(recipient: string, templates: SMSTemplate): Promise<boolean> {
	const lastSend = utils.local.get('lastTestTextSend');
	const now = Date.now();
	const COOLDOWN = 30 * 1000;
	if (lastSend && now - lastSend < COOLDOWN) {
		const timeRemaining = Math.round((COOLDOWN - (now - lastSend)) / 1000);
		utils?.toaster?.addToast?.({
			message: `Please wait ${timeRemaining} seconds before sending another test phone`,
			type: 'danger',
		});
		return false;
	}

	const phone = recipient?.trim();
	if (!phone) {
		utils?.toaster?.addToast?.({
			message: 'Missing phone to send test to',
			type: 'danger',
		});
		return false;
	}

	if (!utils.phoneRex.test(phone)) {
		utils?.toaster?.addToast?.({
			message: 'Invalid phone number',
			type: 'danger',
		});
		return false;
	}

	const [_, error] = await safeFetchAPI(`/campaign/full/preview/:uid?sendToPhone=${phone}`, {
		payload: { id: 'DNE', templates },
		method: 'POST',
	});

	if (error) {
		utils?.toaster?.addToast?.({
			message: error,
			type: 'danger',
		});
		return false;
	}

	utils?.toaster?.addToast?.({
		message: `Test phone sent to ${phone}`,
		type: 'success',
	});
	utils.local.set('lastTestTextSend', now);
	return true;
}

/**
 * Checks if the campaign has been sent
 * @param campaign
 * @returns true if the campaign has been sent
 */
export function hasCampaignSent(campaign: Partial<Campaign> & { lastMessage?: number }): boolean {
	return !!campaign.lastMessage && !!campaign.blast && !(utils.isLocal() || utils.isStaging());
}

/**
 * Cleans the campaign for forecast
 * @param campaign
 * @param noChannel - If true, the campaign will not be cleaned of channels
 * @returns the cleaned campaign
 */
export function cleanForecastableCampaign(campaign: Partial<Campaign>, noChannel?: boolean): Partial<Campaign> {
	const copy: Campaign = utils.clone(campaign || {});
	// return copy
	const cleaned: Partial<Campaign> = {
		id: copy.id || '-1',
		storeIDHash: copy.storeIDHash,
		storeIDs: copy.storeIDs,
		audiences: copy.audiences,
		exclude: copy.exclude,
		optinCampaign: copy.optinCampaign,
		emailOptinCampaign: copy.emailOptinCampaign,
		abVariants: copy.abVariants,
		//     scheduled: copy.scheduled,
		//     blast: copy.blast,
		//     dateRange: copy.dateRange,
		//     sendWindow: copy.sendWindow,
		//     sendAtTimePredicted: copy.sendAtTimePredicted,
		//     triggers: copy.triggers,
	};

	// Remove all channels that are empty
	if (!noChannel) {
		for (const channel of CampaignChannelsMeta) {
			const channelData = copy[channel as CampaignChannelType];
			if (isChannelEmpty(channelData)) continue;
			cleaned[channel as CampaignChannelType] = {
				inUse: true,
				...(channel === CampaignChannelType.Text && {
					msgContainer: copy.templates?.msgContainer,
					body: copy.templates?.body,
					isMMS: copy.templates?.isMMS,
					mmsOnly: copy.templates?.mmsOnly,
				}),
				...(channel === CampaignChannelType.Voice && {
					ttv: copy.voiceTemplate?.ttv,
					voiceFile: copy.voiceTemplate?.voiceFile,
				}),
			} as CampaignChannelUnion;
		}
	}

	// Clean off any undefined values, or empty arrays
	return utils.trimObject(cleaned, true, false, true, true);
}

export function hasChannelSpecificSchedule(campaign?: Partial<Campaign>, channel?: CampaignChannelType): boolean {
	if (!campaign) return false;

	if (channel) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) return false;
		return hasSchedule(template.schedule);
	}

	const channels = getCampaignsChannels(campaign, true);

	for (const [channel, _] of channels) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) continue;
		if (hasSchedule(template.schedule)) return true;
	}

	return false;
}

function hasSchedule(schedule: CampaignSchedule | undefined): boolean {
	if (!schedule) return false;
	if (schedule.dateRange && Object.keys(schedule.dateRange).length > 0) {
		return true;
	}

	if (schedule.scheduled && schedule.scheduled > 0) {
		return true;
	}

	if (schedule.recurDays && schedule.recurDays > 0) {
		return true;
	}

	if (schedule.sendWindow && (schedule.sendWindow.start || schedule.sendWindow.end)) {
		return true;
	}

	return false;
}

export function getCampaignSchedule(campaign?: Partial<Campaign>, channel?: CampaignChannelType): CampaignSchedule | undefined {
	if (!campaign) return undefined;
	if (!channel) {
		return {
			dateRange: campaign.dateRange,
			scheduled: campaign.scheduled ?? 0,
			recurDays: campaign.recurDays ?? -1,
			blast: campaign.blast ?? false,
			sendWindow: campaign.sendWindow,
		};
	}
	if (channel) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) return getCampaignSchedule(campaign);
		return template.schedule;
	}

	return undefined;
}

export function hasChannelSpecificMessageCaps(campaign?: Partial<Campaign>, channel?: CampaignChannelType, selectKeys?: (keyof CampaignMessageCaps)[]): boolean {
	if (!campaign) return false;

	if (channel) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) return false;
		return hasMessageCaps(template?.messageCaps, selectKeys);
	}

	const channels = getCampaignsChannels(campaign, true);

	for (const [channel, _] of channels) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) continue;
		if (hasMessageCaps(template?.messageCaps, selectKeys)) return true;
	}

	return false;
}

function hasMessageCaps(messageCaps: CampaignMessageCaps | undefined, selectKeys?: (keyof CampaignMessageCaps)[]): boolean {
	if (!messageCaps) return false;
	const showKey = (key: keyof CampaignMessageCaps) => !selectKeys || selectKeys?.includes(key);
	if (showKey('sendAtTimePredicted') && messageCaps.sendAtTimePredicted) return true;
	if (showKey('capPerDay') && messageCaps.capPerDay) return true;
	if (showKey('lifetimeCap') && messageCaps.lifetimeCap) return true;
	return false;
}

export function getCampaignMessageCaps(campaign?: Partial<Campaign>, channel?: CampaignChannelType): CampaignMessageCaps | undefined {
	if (!campaign) return undefined;
	if (!channel) {
		return {
			capPerDay: campaign.capPerDay ?? 0,
			lifetimeCap: campaign.lifetimeCap ?? 0,
			sendAtTimePredicted: campaign.sendAtTimePredicted ?? false,
		};
	}
	if (channel) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) return getCampaignMessageCaps(campaign);
		return template.messageCaps;
	}

	return undefined;
}

export function hasChannelSpecificTargeting(campaign?: Partial<Campaign>, channel?: CampaignChannelType): boolean {
	if (!campaign) return false;

	if (channel) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) return false;
		return hasTargeting(template?.targeting);
	}

	const channels = getCampaignsChannels(campaign, true);

	for (const [channel, _] of channels) {
		const template = campaign[channel as CampaignChannelType];
		if (!template) continue;
		if (hasTargeting(template?.targeting)) return true;
	}

	return false;
}

function hasTargeting(targeting: CampaignTargeting | undefined): boolean {
	if (!targeting) return false;
	if (targeting.audiences && targeting.audiences.length > 0) return true;
	if (targeting.exclude && targeting.exclude.length > 0) return true;
	if (targeting.surveys && targeting.surveys.length > 0) return true;
	if (targeting.signupForms && targeting.signupForms.length > 0) return true;
	if (targeting.storeIDs && targeting.storeIDs.length > 0) return true;
	return false;
}

export function getCampaignTargeting(campaign?: Partial<Campaign>, channel?: CampaignChannelType): CampaignTargeting | undefined {
	if (!campaign) return undefined;
	if (!channel) {
		return {
			audiences: campaign.audiences ?? [],
			exclude: campaign.exclude ?? [],
			surveys: campaign.surveys ?? [],
			signupForms: campaign.signupForms ?? [],
			storeIDs: campaign.storeIDs ?? [],
		};
	}
	return campaign[channel as CampaignChannelType]?.targeting;
}

type CampaignScheduleWithEnableQueue = CampaignSchedule & { enableQueue?: boolean };

export type CampaignScheduleTypeMeta = {
	icon: IconType;
	title: string;
	description: string;
	isShown?: (campaign?: Partial<Campaign>) => boolean;
	isSelected: (schedule?: Partial<CampaignScheduleWithEnableQueue>) => boolean;
	applyToCampaign: (schedule?: Partial<CampaignScheduleWithEnableQueue>) => Partial<CampaignScheduleWithEnableQueue>;
};

export const CampaignScheduleTypesMeta: Record<CampaignScheduleType, CampaignScheduleTypeMeta> = {
	blast: {
		icon: 'ROCKET',
		title: 'Blast',
		description: 'Campaign starts sending to all recipients at the defined start time',
		isSelected: (campaign?: Partial<Campaign>): boolean => {
			if (campaign?.enableQueue) return false;
			const isBlast = !!campaign?.blast || (campaign?.blast === undefined && !campaign?.id);
			return isBlast && !campaign?.dateRange?.active;
		},
		applyToCampaign: (schedule?: Partial<CampaignScheduleWithEnableQueue>): Partial<CampaignScheduleWithEnableQueue> => {
			if (!schedule) return schedule ?? {};
			// campaign.scheduled = 0;
			schedule.blast = true;
			schedule.enableQueue = false;
			schedule.recurDays = -1;
			schedule.dateRange = undefined;
			return schedule;
		},
	},
	ongoing: {
		icon: 'REFRESH',
		title: 'Ongoing',
		description: 'Campaign starts sending to recipients as soon as they are added after the defined start time',
		isSelected: (campaign?: Partial<Campaign>): boolean => {
			const isBlast = !!campaign?.blast || (campaign?.blast === undefined && !campaign?.id);
			return !isBlast && !campaign?.dateRange?.active;
		},
		applyToCampaign: (schedule?: Partial<CampaignScheduleWithEnableQueue>): Partial<CampaignScheduleWithEnableQueue> => {
			if (!schedule) return schedule ?? {};
			// campaign.scheduled = 0;
			schedule.blast = false;
			schedule.enableQueue = false;
			schedule.recurDays = -1;
			schedule.dateRange = { active: false, start: 0, end: 0 };
			return schedule;
		},
	},
	dateRange: {
		icon: 'CALENDAR',
		title: 'Start and End',
		description: 'Campaign starts sending to recipients at the defined start time and anyone until the end time',
		isSelected: (campaign?: Partial<Campaign>): boolean => {
			return !!campaign?.dateRange && !!campaign?.dateRange?.active;
		},
		applyToCampaign: (schedule?: Partial<CampaignScheduleWithEnableQueue>): Partial<CampaignScheduleWithEnableQueue> => {
			if (!schedule) return schedule ?? {};
			schedule.enableQueue = false;
			schedule.scheduled = 0;
			schedule.recurDays = -1;
			schedule.blast = false;
			if (schedule.dateRange) schedule.dateRange.active = true;
			else schedule.dateRange = { active: true, start: 0, end: 0 };
			return schedule;
		},
	},
	queue: {
		icon: 'SEND_NEW',
		title: 'Queue',
		description: 'Send messages to clients one at a time',
		isShown: (campaign?: Partial<Campaign>) => {
			if (adminUtils.canSeeAll() && utils.isLocal()) return true;
			if (!hasCampaignChannel(campaign, CampaignChannelType.Text)) return false;
			return utils.isDevUID() || utils.user.profile?.industry === 'political';
		},
		isSelected: (campaign?: Partial<Campaign>): boolean => {
			return !!campaign?.enableQueue;
		},
		applyToCampaign: (schedule?: Partial<CampaignScheduleWithEnableQueue>): Partial<CampaignScheduleWithEnableQueue> => {
			if (!schedule) return schedule ?? {};
			schedule.enableQueue = true;
			schedule.scheduled = Math.floor(Date.now() / 1000) - timeUtils.units.MINUTE_S * 5;
			schedule.blast = true;
			schedule.recurDays = -1;
			schedule.dateRange = undefined;
			return schedule;
		},
	},
};

export function cleanCampaignObject(campaign?: Partial<Campaign>): Partial<Campaign> {
	let copy = utils.clone(campaign ?? {});

	copy = removeUnusedProductSlugs(copy);
	copy = cleanEmailTemplate(copy);
	if (copy.templates?.isMMS && utils.hasRestrictedIndustry10DLC()) {
		copy = lockMMSURL(copy);
	}

	return copy;
}

export function lockMMSURL(campaign: Partial<Campaign>): Partial<Campaign> {
	if (campaign?.templates) {
		if (utils.user.optinImage !== '') {
			campaign.templates.mmsMediaURL = utils.user.optinImage;
		} else {
			campaign.templates.mmsMediaURL = `https://lab.alpineiq.com/avatar/${utils.uid}/-1?cacheBuster=${Date.now()}&mms=true`;
		}
		campaign.templates.mediaContentType = 'image';
	}
	return campaign;
}

export function removeUnusedProductSlugs(campaign: Partial<Campaign>): Partial<Campaign> {
	if (campaign.templates?.slugs?.length) {
		const textHtml = campaign.templates.landingHTML || '';
		// Check that the textHTML contains the slugs, if not remove them
		campaign.templates.slugs = campaign.templates.slugs.filter((slug) => textHtml.includes(slug));
	}

	if (campaign.emailTemplate?.slugs?.length) {
		const emailHtml = campaign.emailTemplate.html || '';
		// Check that the emailHTML contains the slugs, if not remove them
		campaign.emailTemplate.slugs = campaign.emailTemplate.slugs.filter((slug) => emailHtml.includes(slug));
	}
	return campaign;
}

export function cleanEmailTemplate(campaign: Partial<Campaign>): Partial<Campaign> {
	// &amp; is breaking the links so we need to remove it.
	if (campaign.emailTemplate?.html) {
		campaign.emailTemplate.html = campaign.emailTemplate.html?.replaceAll(/&amp;/g, '&');
	}
	return campaign;
}

const dynamicRegex = new RegExp(/\{\{dynamic-[A-Za-z0-9]+-[A-Za-z0-9]+-[A-Za-z0-9]+\}\}/gi);
const isDynamic = (html: string) => dynamicRegex.test(html || '') || (html || '')?.includes(`id="dynamic`);

export function validateSingleCampaign(campaign?: Partial<Campaign>): string[] {
	const errors: string[] = [];

	if (!campaign) return errors;

	// Draft campaigns require basically no validation
	if (campaign?.draft) {
		return errors;
	}

	const hasAudienceTargets = campaign.audiences?.length || campaign.exclude?.length || campaign.surveys?.length || campaign.signupForms?.length;
	const hasSurveyTargets = campaign.surveys?.length;
	const hasSignupFormTargets = campaign.signupForms?.length;

	if (!campaign.name) errors.push('Campaign must have a name');
	if (!hasAudienceTargets && !hasSurveyTargets && !hasSignupFormTargets) {
		errors.push('Campaign must have at least one audience, survey, or signup form');
	}

	// If campaign is optinCampin don't allow HTML landing pages
	if (campaign.optinCampaign && campaign.templates?.landingType === 'HTML') {
		errors.push('You cannot use HTML landing pages with opt-in campaigns!');
	}

	const hasQueue = campaign.enableQueue;
	const hasLifetimeCap = (campaign.lifetimeCap ?? 0) > 0;
	// Blast date cannot be in past (if set)
	// If they have lifetime cap, or queue enabled, we ignore the send date being in the past
	if (!hasQueue && !hasLifetimeCap && campaign.scheduled && campaign.scheduled < Date.now() / 1000) {
		errors.push('Scheduled campaign messaging date cannot be in the past');
	}

	// If campaign has hashStoreId's then it is a sponsored campaign, and there cannot be store ID's and audiences
	if (campaign.storeIDHash && Object.keys(campaign.storeIDHash).length) {
		// If audiences is not just ['all] return err
		if ((campaign.audiences || []).length !== 1 && (campaign.audiences || [])[0] !== 'all') {
			errors.push('You must target "All subscribed users" when creating a campaign targeting connected stores.');
		}
		// If they have selected stores return err
		if (campaign.storeIDs?.length) {
			errors.push('You cannot select stores when creating a campaign targeting connected stores.');
		}
		// same for excluded audiences
		if (campaign.exclude?.length) {
			errors.push('You cannot exclude audiences when creating a campaign targeting connected stores.');
		}
	}

	// Retailer specific checks
	if (!campaign.isCoMarketing) {
		if (campaign?.emailTemplate && !campaign?.emailTemplate.from) {
			errors.push('You must input a "From email" inside your email template if you wish to send emails!');
		}
	}

	if (campaign.templates) {
		if (campaign.templates.msgContainer) {
			const hasMsgContainer = campaign.templates.msgContainer;
			const hasBody = campaign.templates.body;
			if (hasMsgContainer && hasBody && !campaign.templates.landingType) {
				errors.push('Campaign has invalid "Landing Type" (Can occur when cloning legacy campaigns)');
			}
		} else {
			errors.push('You must select a "Message Container" inside your text template before saving');
		}
	}

	// Check dynamic content
	const checkTemplates = [
		{
			type: 'Text Channel',
			html: campaign.templates?.landingHTML,
			json: campaign.templates?.landingDesign,
			dynamicContent: campaign.templates?.dynamicContent,
		},
		{
			type: 'Email Channel',
			html: campaign.emailTemplate?.html,
			json: campaign.emailTemplate?.json,
			dynamicContent: campaign.emailTemplate?.dynamicContent,
		},
	];
	for (const info of checkTemplates) {
		if (info.html) {
			const htmlHasDynamicMacros = isDynamic(info.html);
			const sampleDataEnabled = JSON.stringify(info.json || {}).includes(`"showSampleData":true,`);
			const hasConfiguredContent = info?.dynamicContent || Object.keys(info?.dynamicContent || {}).length > 0;

			// If they have dynamic content, but no dynamic macros, throw an error
			if (hasConfiguredContent && !htmlHasDynamicMacros) {
				errors.push(`Add dynamic content before saving your ${info.type} with a "Product Feed" filter!`);
			}

			// If they have dynamic macros, but no dynamic content, throw an error
			if (htmlHasDynamicMacros && !hasConfiguredContent) {
				errors.push(`You must configure your dynamic content before saving your ${info.type} with dynamic macros!`);
			}

			if (sampleDataEnabled) {
				errors.push('You must disable sample dynamic content data before saving! (You may need to toggle it on/off again)');
			}
		}
	}

	return errors;
}

export enum CampaignEditingMethod {
	DEFAULT = 'default',
	EDITING_SPONSORING = 'editing_sponsoring', // Sponsor is editing their sponsored template
	PREVIEW_SPONSORED = 'preview_sponsored', // Installer is previewing the sponsored template
	INSTALLER_EDITING_INSTALLED = 'editing_installed', // Installer is editing the installed template
	SPONSOR_EDITING_INSTALLED = 'sponsor_editing_installed', // Sponsor is editing the installed template
	REVIEW_SPONSOR_CHANGES = 'review_sponsor_changes', // Installer is reviewing the sponsor's changes
	REVIEW_INSTALLER_CHANGES = 'review_installer_changes', // Sponsor is reviewing the installer's changes
}

export function getCampaignEditingMethodType(
	campaign?: Partial<Campaign>,
	installedTemplate?: Partial<InstalledCoMarketingTemplate>,
	sponsoredTemplate?: Partial<SponsoredCoMarketingTemplate>,
): CampaignEditingMethod {
	if (campaign?.isCoMarketing) {
		if (campaign.userID === utils.uid) {
			return CampaignEditingMethod.EDITING_SPONSORING;
		}
		return CampaignEditingMethod.PREVIEW_SPONSORED;
	}

	if (installedTemplate?.userID === utils.uid) {
		return CampaignEditingMethod.INSTALLER_EDITING_INSTALLED;
	}

	if (installedTemplate?.installedFromUserID === utils.uid) {
		return CampaignEditingMethod.SPONSOR_EDITING_INSTALLED;
	}

	return CampaignEditingMethod.DEFAULT;
}

export function getEditorColor(editingMethod?: CampaignEditingMethod) {
	if (editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES || editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES) {
		return 'var(--warning, #C74800)';
	}

	if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED || editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
		return 'var(--info, #006DED)';
	}

	return '';
}

export function isEditingMode(editingMethod?: CampaignEditingMethod) {
	if (editingMethod === CampaignEditingMethod.DEFAULT) return false;
	if (editingMethod === CampaignEditingMethod.EDITING_SPONSORING) return false;
	return true;
}

export function respectEditingMode(editingMethod?: CampaignEditingMethod) {
	if (editingMethod === CampaignEditingMethod.DEFAULT) return false;
	if (editingMethod === CampaignEditingMethod.EDITING_SPONSORING) return false;
	if (editingMethod === CampaignEditingMethod.PREVIEW_SPONSORED) return true;
	if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) return true;
	if (editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) return true;
	if (editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES) return true;
	if (editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES) return true;
	return true;
}

export function canTestSendTextMessages() {
	if (utils.isDispojoy() || utils.isMessageDigital() || utils.isPolitical()) {
		return true;
	}
	if (utils.isPolitical()) {
		return true;
	}
	if (utils.isDevUID()) {
		return true;
	}
	return utils.hasApprovedTelnyxCampaign() || utils.hasInfoBipCampaignID();
}
